import type { GameState } from '@pacmates/engine';

/** The discrete sound-worthy things that can happen between two consecutive
 * authoritative snapshots. Pure derivation off real `GameState` fields --
 * SPEC.md §1.1's "single source of truth" applies here too: no client-side
 * event bookkeeping, just diff the two snapshots the server actually sent. */
export type SoundEvent = 'chomp' | 'energizer' | 'ghostEaten' | 'fruit' | 'death' | 'extraLife' | 'levelUp';

/** Compares two authoritative `GameState` snapshots and returns the sound
 * events that occurred going from `prev` to `cur`. Pure -- no Web Audio, no
 * globals, no side effects -- so this is fully unit-testable without a
 * browser AudioContext (see sound.ts for the player that consumes this).
 *
 * `prev === null` means `cur` is the very first snapshot this client has
 * ever seen (nothing to diff against) -- always `[]`, never guess a sound
 * for a round-in-progress join.
 */
export function detectSoundEvents(prev: GameState | null, cur: GameState): SoundEvent[] {
  if (!prev) return [];

  const events: SoundEvent[] = [];

  // Fright just started this tick -- an energizer was eaten. Check this
  // BEFORE chomp so the pellet-count drop that an energizer ALSO causes
  // (an energizer occupies a pellet-grid cell too) is reported once, as
  // 'energizer', not duplicated as a plain 'chomp'.
  const energizerStarted = prev.frightLeft <= 0 && cur.frightLeft > 0;
  if (energizerStarted) {
    events.push('energizer');
  } else if (cur.pelletsRemaining < prev.pelletsRemaining) {
    events.push('chomp');
  }

  // A ghost transitioned INTO 'eaten' mode this tick. Multiple ghosts eaten
  // in the same tick (a fright-chain) still emit just ONE 'ghostEaten' --
  // spamming one per ghost would be an audible mess for a single moment.
  const ghostJustEaten = cur.ghosts.some((g, i) => {
    const prevGhost = prev.ghosts[i];
    return prevGhost && prevGhost.mode !== 'eaten' && g.mode === 'eaten';
  });
  if (ghostJustEaten) {
    events.push('ghostEaten');
  }

  const prevFruits = prev.players[0]!.fruitsEaten + prev.players[1]!.fruitsEaten;
  const curFruits = cur.players[0]!.fruitsEaten + cur.players[1]!.fruitsEaten;
  if (curFruits > prevFruits) {
    events.push('fruit');
  }

  const died = cur.players.some((p, i) => {
    const prevP = prev.players[i];
    return prevP && (p.lives < prevP.lives || p.deaths > prevP.deaths);
  });
  if (died) {
    events.push('death');
  }

  const gainedLife = cur.players.some((p, i) => {
    const prevP = prev.players[i];
    return prevP && p.lives > prevP.lives;
  });
  if (gainedLife) {
    events.push('extraLife');
  }

  if (cur.level > prev.level) {
    events.push('levelUp');
  }

  return events;
}
